import { ReactNode, useId } from 'react';
import { RegisterOptions } from 'react-hook-form';
import { DescriptionProps, LabelProps } from '../use_input.hook';

interface CommonInputSelectProps {
	id?: string;
	name?: string;
	helperText?: string;
	invalid?: boolean;
	invalidText?: string;
	readOnly?: boolean;
	required?: boolean;
	optional?: boolean;
	value?: string;
	defaultValue?: string;
	onValueChange?(value: string): void;
}

interface UseInputSelectProps extends CommonInputSelectProps {
	label: string;
	hideLabel?: boolean;
	placeholder?: string;
	description?: string;
	options?: RegisterOptions;
}

interface HelperTextProps {
	id: string;
	invalid: boolean;
	children: ReactNode;
}

interface InputSelectProps extends CommonInputSelectProps {}

interface InputSelectTriggerProps {
	id: string;
	'aria-label'?: string;
	'aria-describedby'?: string;
	'aria-labelledby'?: string;
	placeholder?: string;
}

interface UseInputSelectReturn {
	props: InputSelectProps;
	triggerProps: InputSelectTriggerProps;
	descriptionProps: DescriptionProps;
	helpTextProps: HelperTextProps;
	labelProps: LabelProps;
}

export function useInputSelect({
	id,
	name,
	label,
	hideLabel,
	invalid,
	readOnly,
	invalidText,
	helperText,
	onValueChange,
	required,
	value,
	defaultValue,
	placeholder,
	description,
	optional,
}: UseInputSelectProps): UseInputSelectReturn {
	// Get unique ID for the input element
	let inputId = useId();

	if (id) {
		inputId = id;
	}

	// Get id for the label
	const labelId = `${inputId}-label`;

	// Get id for help text component
	const helpTextId = `${inputId}-helpText`;

	// Get id for description component
	const descriptionId = `${inputId}-description`;

	return {
		props: {
			id: inputId,
			name,
			invalid,
			readOnly,
			onValueChange,
			required,
			value,
			defaultValue,
		},
		triggerProps: {
			id: inputId,
			'aria-labelledby': !hideLabel ? labelId : undefined,
			'aria-label': hideLabel ? label : undefined,
			'aria-describedby': [descriptionId, helpTextId].join(' '),
			placeholder,
		},
		labelProps: {
			id: labelId,
			optional: required ? false : optional,
			htmlFor: inputId,
			children: hideLabel ? null : label,
		},
		descriptionProps: {
			id: descriptionId,
			children: description ?? undefined,
		},
		helpTextProps: {
			id: helpTextId,
			invalid: !!(invalid && invalidText),
			children: invalid && invalidText ? invalidText : helperText,
		},
	};
}
